import { ApiService } from '@/apiConnection/ApiService';
import { Urls } from '@/apiConnection/urls';
import { CustomInputComponent } from '@/components/custom-input/custom-input.component';
import { CustomModalComponent } from '@/components/custom-modal/custom-modal.component';
import { CustomSelectComponent } from '@/components/custom-select/custom-select.component';
import { LoadingComponent } from '@/components/loading/loading.component';
import { Utils } from '@/utils/utils';
import { environment } from '../../../../environments/environment';
import { Component, Input, Output, EventEmitter, ChangeDetectorRef, signal } from '@angular/core';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

const DOCUMENT_TYPES = [
  { id: 'albaran', name: 'Albarán' },
  { id: 'ticket', name: 'Ticket' },
];

// Mirrors IntegrationsController::IMPORTABLE_STATUSES (erp-backend).
const ORDER_STATUSES = [
  { id: 'pending', name: 'Pendiente de pago' },
  { id: 'processing', name: 'Procesando' },
  { id: 'on-hold', name: 'En espera' },
  { id: 'completed', name: 'Completado' },
];

/**
 * Create/edit a WooCommerce shop. Saved with ApiService.post/put, never `save()`:
 * the backend tests the connection on every create, so queueing it offline would
 * report a success that may later fail.
 */
@Component({
  selector: 'app-integration-form',
  imports: [
    CustomModalComponent,
    ReactiveFormsModule,
    CustomInputComponent,
    CustomSelectComponent,
    LoadingComponent,
  ],
  templateUrl: './integration-form.component.html',
  styleUrl: './integration-form.component.scss',
})
export class IntegrationFormComponent {
  @Input() id: number | string = 0;
  @Output() closeFunction = new EventEmitter<any>();
  @Output() acceptFunction = new EventEmitter<any>();

  public loading = signal(false);
  public saving = signal(false);

  public saveForm: FormGroup = new FormGroup({});

  public documentTypes = DOCUMENT_TYPES;
  public orderStatuses = ORDER_STATUSES;

  public dropdowns: any = {
    offices: [],
    clients: [],
    pay_methods: [],
    products: [],
    price_fields: [],
    taxes: [],
  };

  constructor(
    private apiService: ApiService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.createForm();
    this.getDropdowns();
  }

  get isEdit(): boolean {
    return this.id != 0;
  }

  get payMethodRows() {
    return this.saveForm.get('pay_method_rows') as FormArray;
  }

  get taxMapRows() {
    return this.saveForm.get('tax_map_rows') as FormArray;
  }

  acceptFormFunction() {
    this.saveEntity();
  }

  closeFormFunction() {
    this.closeFunction.emit();
  }

  createForm() {
    // Keys are write-only: on edit they stay empty and are only sent if retyped.
    const keyValidators = this.isEdit ? [] : [Validators.required];

    this.saveForm = new FormGroup({
      name: new FormControl('', [Validators.required]),
      store_url: new FormControl('', [Validators.required,
        Validators.pattern(environment.allowLocalStores ? /^https?:\/\/.+/i : /^https:\/\/.+/i)]),
      consumer_key: new FormControl('', keyValidators),
      consumer_secret: new FormControl('', keyValidators),
      enabled: new FormControl(true),
      office_id: new FormControl(null),
      default_client_id: new FormControl(null),
      default_pay_id: new FormControl(null),
      pay_method_rows: new FormArray([]),
      tax_map_rows: new FormArray([]),
      price_field: new FormControl('price_ticket', [Validators.required]),
      erp_prices_include_tax: new FormControl(true),
      sync_categories: new FormControl(true),
      sync_stock: new FormControl(true),
      query_string_auth: new FormControl(false),
      order_document_type: new FormControl('albaran', [Validators.required]),
      import_statuses: new FormControl(['processing', 'completed'], [Validators.required]),
      shipping_product_id: new FormControl(null),
    });
  }

  addPayMethodRow(gateway = '', payId: number | null = null) {
    this.payMethodRows.push(new FormGroup({
      gateway: new FormControl(gateway, [Validators.required]),
      pay_id: new FormControl(payId, [Validators.required]),
    }));
  }

  removePayMethodRow(index: number) {
    this.payMethodRows.removeAt(index);
  }

  getDropdowns() {
    this.loading.set(true);
    const config = { offices: {}, clients: {}, pay_methods: {}, products: {}, companies: {} };
    this.apiService.getSelectors(Urls.urls.selectors, config).subscribe({
      next: (data: any) => {
        this.dropdowns.offices = data.offices ?? [];
        this.dropdowns.clients = data.clients ?? [];
        this.dropdowns.pay_methods = data.pay_methods ?? [];
        this.dropdowns.products = (data.products ?? []).map((p: any) => ({
          ...p,
          label: p.code ? `${p.code} - ${p.name}` : p.name,
        }));

        const company = data.companies?.[0] ?? {};
        const tariffs = JSON.parse(company.invoice_tariffs || '[]');
        this.dropdowns.price_fields = [
          { id: 'price_ticket', name: 'Precio de venta (ticket)' },
          { id: 'medium_price', name: 'Precio medio de compra' },
          ...tariffs.map((t: any) => ({ id: `more_prices.${t.id}`, name: `Tarifa ${t.id} (${t.quotient})` })),
        ];

        this.dropdowns.taxes = JSON.parse(company.tax_config || '[]');
        this.taxMapRows.clear();
        this.dropdowns.taxes.forEach((tax: any) => this.taxMapRows.push(new FormGroup({
          tax_id: new FormControl(tax.id),
          label: new FormControl(`IVA ${Math.round((Number(tax.quotient) - 1) * 100)}%`),
          tax_class: new FormControl(''),
        })));

        if (this.isEdit) {
          this.getById(this.id);
        } else {
          this.loading.set(false);
          this.cdr.detectChanges();
        }
      },
      error: (error: any) => {
        Utils.notification.alert(Utils.extractErrorMessage(error) || 'Error al cargar los desplegables');
        this.loading.set(false);
      },
    });
  }

  getById(id: number | string) {
    this.apiService.getById(Urls.urls.integrations, id).subscribe({
      next: (data: any) => {
        const settings = data.settings ?? {};
        this.saveForm.patchValue({
          name: data.name,
          store_url: data.store_url,
          enabled: data.enabled,
          office_id: data.office_id,
          default_client_id: data.default_client_id,
          ...settings,
        });

        const payMap = data.pay_method_map ?? {};
        Object.entries(payMap).forEach(([gateway, payId]) => {
          if (gateway === 'default') {
            this.saveForm.patchValue({ default_pay_id: payId });
          } else {
            this.addPayMethodRow(gateway, payId as number);
          }
        });

        const taxMap = settings.tax_map ?? {};
        this.taxMapRows.controls.forEach(row => {
          row.patchValue({ tax_class: taxMap[row.value.tax_id] ?? '' });
        });

        this.loading.set(false);
        this.cdr.detectChanges();
      },
      error: (error: any) => {
        Utils.notification.alert(Utils.extractErrorMessage(error) || 'Error al obtener la integración');
        this.loading.set(false);
      },
    });
  }

  private buildPayload() {
    const v = this.saveForm.value;

    const pay_method_map: Record<string, number> = {};
    v.pay_method_rows.forEach((row: any) => pay_method_map[row.gateway.trim()] = row.pay_id);
    if (v.default_pay_id) {
      pay_method_map['default'] = v.default_pay_id;
    }

    const tax_map: Record<string, string> = {};
    v.tax_map_rows.forEach((row: any) => {
      if (row.tax_class?.trim()) {
        tax_map[row.tax_id] = row.tax_class.trim();
      }
    });

    const payload: any = {
      name: v.name,
      store_url: v.store_url.trim(),
      enabled: v.enabled,
      office_id: v.office_id,
      default_client_id: v.default_client_id,
      pay_method_map,
      settings: {
        price_field: v.price_field,
        erp_prices_include_tax: v.erp_prices_include_tax,
        sync_categories: v.sync_categories,
        sync_stock: v.sync_stock,
        query_string_auth: v.query_string_auth,
        order_document_type: v.order_document_type,
        import_statuses: v.import_statuses,
        shipping_product_id: v.shipping_product_id,
        tax_map,
      },
    };

    if (v.consumer_key?.trim()) {
      payload.consumer_key = v.consumer_key.trim();
    }
    if (v.consumer_secret?.trim()) {
      payload.consumer_secret = v.consumer_secret.trim();
    }

    return payload;
  }

  saveEntity() {
    if (!this.saveForm.valid) {
      this.saveForm.markAllAsTouched();
      Utils.notification.alert('Debe completar todos los campos obligatorios');
      return;
    }

    this.saving.set(true);
    const payload = this.buildPayload();
    const request$ = this.isEdit
      ? this.apiService.put(`${Urls.urls.integrations}/${this.id}`, payload)
      : this.apiService.post(Urls.urls.integrations, payload);

    request$.subscribe({
      next: (response: any) => {
        Utils.notification.success(this.isEdit ? 'Integración actualizada' : 'Tienda conectada');
        this.acceptFunction.emit(response);
        this.closeFormFunction();
      },
      error: (error: any) => {
        this.saving.set(false);
        Utils.notification.alert(Utils.extractErrorMessage(error, 'store_url') || 'Error al guardar la integración');
      },
      complete: () => {
        this.saving.set(false);
      }
    });
  }

}
